import { BottomAction } from '@euler/app/components/BottomAction';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useQuotationFormContext } from '@euler/app/flows/task/pages/quotation/components/quotation-form/Context';
import { Center } from '@euler/components/Center';
import { Input } from '@euler/components/form/Input';
import { InputNumber } from '@euler/components/form/InputNumber';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { FormController, useFormController } from '@euler/lib/form/controller';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { QuotationItemType } from '@euler/model/enum';
import { QuotationConstructionItemStaged } from '@euler/model/task-detail';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useTheme } from 'styled-components';
import invariant from 'tiny-invariant';
import { QuotationFormElements } from './Elements';

const { Section, Row, useStyles } = QuotationFormElements;

type EditorRef = {
  controller: FormController<FormData>;
};

type FormData = {
  name: string;
  price?: number;
  discountPrice?: number;
  code?: string;
  discountRemark?: string;
  remark?: string;
};

type Props = {
  staged?: QuotationConstructionItemStaged;
  isNamePermitted: (name: string) => boolean;
};

export const ConstructionItemEditor = memo(
  forwardRef<EditorRef, Props>(({ staged, isNamePermitted }, ref) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const styles = useStyles();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    const {
      inspection: {
        customIssue: { placeholderColor },
      },
    } = theme;

    const formController = useFormController<FormData>({
      name: {
        required: true,
        defaultValue: staged?.name ?? '',
        validators: [
          value => {
            if (!value.trim()) return true;
            if (!isNamePermitted(value)) {
              return { error: '施工项目名称已存在' };
            }
            return true;
          },
        ],
      },
      price: {
        required: false,
        defaultValue: staged?.price,
        validators: [
          value => {
            if (value == null) return true;
            if (value < 0) {
              return {
                error: '工时费不能小于0',
              };
            }
            return true;
          },
        ],
      },
      discountPrice: {
        defaultValue: staged?.discountPrice,
        validators: [
          (value, data) => {
            if (value == null || data.price.value == null) {
              return true;
            }
            if (value < 0)
              return {
                error: '折扣工时费不能小于0',
              };
            if (value > data.price.value) {
              return {
                error: '折扣工时费不能大于原工时费',
              };
            }
            return true;
          },
        ],
      },
      code: {
        defaultValue: staged?.code,
      },
      discountRemark: {
        defaultValue: staged?.discountRemark,
      },
      remark: {
        defaultValue: staged?.remark,
      },
    });

    useImperativeHandle(ref, () => ({
      controller: formController,
    }));

    const { form } = formController;

    return (
      <>
        <Section title="施工项目名称">
          <Row>
            <Input
              value={form.name.value}
              errorMsg={form.name.error}
              style={styles.formInput}
              onChangeText={form.name.update}
              onBlur={form.name.onBlur}
              placeholder="例如: 更换机油及机滤"
              placeholderTextColor={placeholderColor}
            />
          </Row>
        </Section>
        <Section title="项目编码">
          <Row>
            <Input
              value={form.code.value}
              errorMsg={form.code.error}
              onChangeText={form.code.update}
              onBlur={form.code.onBlur}
              placeholder="如: SG001"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
            />
          </Row>
        </Section>
        <Section title="工时费">
          <Row>
            <StackLayout
              direction="row"
              justifyContent="space-between"
              divider={
                <View
                  css={`
                    width: ${StyleSheet.hairlineWidth}px;
                    align-self: stretch;
                    background-color: #ddd;
                    margin: ${rpx(-8)}px 0;
                  `}
                />
              }
            >
              <InputNumber
                value={form.price.value}
                errorMsg={form.price.error}
                onChangeValue={form.price.update}
                onBlur={form.price.onBlur}
                placeholder="工时费"
                placeholderTextColor={placeholderColor}
                style={[styles.formInput, { paddingRight: rpx(15) }]}
                containerStyle={{ flex: 1 }}
              />
              <InputNumber
                value={form.discountPrice.value}
                errorMsg={form.discountPrice.error}
                onChangeValue={form.discountPrice.update}
                onBlur={form.discountPrice.onBlur}
                placeholder="优惠后工时费"
                placeholderTextColor={placeholderColor}
                style={[styles.formInput, { paddingLeft: rpx(15) }]}
                containerStyle={{ flex: 1 }}
              />
            </StackLayout>
          </Row>
        </Section>
        <Section title="优惠备注">
          <Row>
            <Input
              value={form.discountRemark.value}
              errorMsg={form.discountRemark.error}
              onChangeText={form.discountRemark.update}
              onBlur={form.discountRemark.onBlur}
              placeholder="优惠备注信息"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
              withVoiceInput={isVoiceInputEnabled}
            />
          </Row>
        </Section>
        <Section title="项目备注">
          <Row>
            <Input
              value={form.remark.value}
              errorMsg={form.remark.error}
              onChangeText={form.remark.update}
              onBlur={form.remark.onBlur}
              placeholder="备注信息"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
              withVoiceInput={isVoiceInputEnabled}
            />
          </Row>
        </Section>
      </>
    );
  }),
);

export const ConstructionItemEditorBottomSheet = memo(() => {
  useToggleAutomaticKeyboard(true);

  const {
    taskManager: { quotationManager },
  } = useTaskContext();
  const { constructionItemBeingEdited$ } = useQuotationFormContext();
  const [constructionItemBeingEdited] = useBehaviorSubject(
    constructionItemBeingEdited$,
  );

  const title =
    constructionItemBeingEdited?.type === 'edit'
      ? '修改施工项目信息'
      : '添加施工项目';

  const { rpx } = useResponsiveContext();
  const { navBarHeight, safeAreaInsets: insets } = useSystemMetrics();

  const ref = useRef<EditorRef>(null);

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: constructionItemBeingEdited != null,
      topMargin: navBarHeight,
      minSnapPoint: '50%',
      mediumSnapPoint: '75%',
      index: 1,
      enableDismissOnClose: true,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss: () => {
        constructionItemBeingEdited$.next(undefined);
      },
    });
  const animatedPosition = useSharedValue(0);
  const height = useGeneralBottomSheetHeight();
  const style = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        animatedPosition.value,
        [(height * 2) / 4, height],
        [1, 0],
      ),
    };
  });

  const onCancel = usePersistFn(() => {
    bottomSheetModalRef.current?.dismiss();
  });

  const isNamePermitted = usePersistFn((name: string) => {
    invariant(constructionItemBeingEdited != null);

    if (constructionItemBeingEdited.type === 'new') {
      return !constructionItemBeingEdited.subject.items.some(
        x => x.name === name,
      );
    }

    const index = constructionItemBeingEdited.index;
    return !constructionItemBeingEdited.subject.items.some(
      (x, i) => index !== i && x.name === name,
    );
  });

  const onDone = usePersistFn(async () => {
    invariant(constructionItemBeingEdited != null);

    const controller = ref.current?.controller;
    if (!controller) return;

    const valid = await controller.validateAll();
    if (!valid) return;

    const data = controller.getData();

    // create or update the material
    quotationManager.produce(draft => {
      const subject = draft.subjects.find(
        x => x.name === constructionItemBeingEdited.subject.name,
      );

      invariant(subject != null);

      const item: QuotationConstructionItemStaged = {
        name: data.name,
        price: data.price!,
        discountPrice: data.discountPrice,
        qty: 1,
        included: true,
        defaultIncluded: true,
        type: QuotationItemType.Construction,
        code: data.code,
        discountRemark: data.discountRemark?.trim(),
        remark: data.remark?.trim(),
        materials: [],
      };

      if (constructionItemBeingEdited.type === 'new') {
        subject.items.push(item);
      } else {
        invariant(subject.items[constructionItemBeingEdited.index] != null);
        item.materials =
          subject.items[constructionItemBeingEdited.index].materials;
        subject.items[constructionItemBeingEdited.index] = item;
      }
    });

    bottomSheetModalRef.current?.dismiss();
  });

  const Footer = usePersistFn((props: BottomSheetFooterProps) => {
    return (
      <BottomSheetFooter {...props}>
        <BottomAction containerStyle={style}>
          <BottomAction.Button
            text="取消"
            variant="default"
            onPress={onCancel}
          />
          <BottomAction.Button
            text="确定"
            variant="primary"
            css={`
              margin-left: ${rpx(15)}px;
            `}
            onPress={onDone}
          />
        </BottomAction>
      </BottomSheetFooter>
    );
  });

  const Header = useCallback(
    () => (
      <Center
        css={`
          padding: ${rpx(10)}px 0;
          border-bottom-width: 1px;
          border-bottom-color: #f1f1f1;
          height: ${rpx(44)}px;
        `}
      >
        <Typography.Label size={rpx(16)} medium color="#333">
          {title}
        </Typography.Label>
      </Center>
    ),
    [rpx, title],
  );

  const ScrollViewComponent =
    Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

  return (
    <>
      <BottomSheetModal
        {...bottomSheetProps}
        footerComponent={Footer}
        animatedPosition={animatedPosition}
        keyboardBehavior="interactive"
        keyboardBlurBehavior="none"
        ref={bottomSheetModalRef}
      >
        <ScrollViewComponent
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(90),
          }}
        >
          <Header />
          <ConstructionItemEditor
            ref={ref}
            key={constructionItemBeingEdited?.key}
            isNamePermitted={isNamePermitted}
            staged={
              constructionItemBeingEdited?.type === 'edit'
                ? constructionItemBeingEdited.item
                : undefined
            }
          />
        </ScrollViewComponent>
      </BottomSheetModal>
    </>
  );
});
